import { useState } from 'react';
import { ActionMenu, ActionList, TextInput } from '@primer/react';
import { PlusIcon, SearchIcon } from '@primer/octicons-react';
import styled from 'styled-components';
import { AddButton, ListToken } from './shared/tokens';
import { SectionContainer, ItemsContainer, ItemsList } from './shared/section-styles';
import { SectionTitleWithGuidance, SectionWarning } from './shared/section-guidance';
import { EnergySource } from '../../../../../../common/model/input/companies/products/ipr';
import { ATTRIBUTE_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';

interface EnergySourcesProps {
  energySources: EnergySourceItem[];
  onEnergySourcesChange: (energySources: EnergySourceItem[]) => void;
  warning?: string;
}

export interface EnergySourceItem {
  id: EnergySource;
  title: string;
}

const ENERGY_SOURCES: EnergySourceItem[] = Object.values(EnergySource).map(value => ({
  id: value,
  title: value,
}));

export function EnergySources({ energySources, onEnergySourcesChange, warning }: EnergySourcesProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const availableEnergySources = ENERGY_SOURCES.filter(
    energySource =>
      !energySources.some(e => e.id === energySource.id) &&
      energySource.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const addEnergySource = (energySource: EnergySourceItem) => {
    onEnergySourcesChange([...energySources, energySource]);
    setIsMenuOpen(false);
    setSearchTerm('');
  };

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const removeEnergySource = (energySource: EnergySourceItem) => {
    onEnergySourcesChange(energySources.filter(e => e.id !== energySource.id));
  };

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title="Energy sources" guidance={ATTRIBUTE_GUIDANCE.energySources} />
      <ItemsContainer>
        <ItemsList>
          {energySources.map(energySource => (
            <ListToken
              key={energySource.id}
              size="medium"
              onRemove={() => {
                removeEnergySource(energySource);
              }}
              text={energySource.title}
            />
          ))}
          <ActionMenu open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
            <ActionMenu.Anchor>
              <AddButton size="medium" leadingVisual={PlusIcon} text="Add energy source" />
            </ActionMenu.Anchor>

            <ActionMenu.Overlay width="medium">
              <SearchContainer>
                <TextInput
                  placeholder="Search energy sources..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  leadingVisual={SearchIcon}
                  size="small"
                />
              </SearchContainer>
              <ScrollableActionListContainer>
                <ActionList>
                  <ActionList.Group title="Select an energy source...">
                    {availableEnergySources.length > 0 ? (
                      availableEnergySources.map(energySource => (
                        <ActionList.Item
                          key={energySource.id}
                          onSelect={() => {
                            addEnergySource(energySource);
                          }}
                        >
                          {energySource.title}
                        </ActionList.Item>
                      ))
                    ) : (
                      <ActionList.Item disabled>
                        {searchTerm ? 'No matching energy sources found' : 'All energy sources added'}
                      </ActionList.Item>
                    )}
                  </ActionList.Group>
                </ActionList>
              </ScrollableActionListContainer>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ItemsList>
      </ItemsContainer>
      <SectionWarning warning={warning} />
    </SectionContainer>
  );
}

const SearchContainer = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-default);
`;

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
